import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { BarChart3, RefreshCw } from 'lucide-react';
import Navigation from '@/components/Navigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const confColor = (c) => c >= 80 ? 'text-brand-lime' : c >= 60 ? 'text-brand-cyan' : 'text-yellow-400';
const rankColor = (r) => r <= 3 ? 'text-brand-lime' : r <= 7 ? 'text-brand-cyan' : 'text-muted-foreground';

const WnbaProjections = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');

  const fetchData = async () => {
    setRefreshing(true);
    try {
      const res = await axios.get(`${API}/wnba/projections`);
      setData(res.data);
    } catch (e) {
      console.error('wnba projections error', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const goToPlayer = (name) => navigate(`/wnba/players?player=${encodeURIComponent(name)}`);

  const filtered = (data?.players || []).filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.team || '').toLowerCase() === search.toLowerCase()
  );

  return (
    <div className="min-h-screen bg-[#0a1628]">
      <Navigation />
      <main className="pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight flex items-center gap-3">
                <BarChart3 className="h-8 w-8 text-orange-400" />
                WNBA Projections
              </h1>
              <p className="text-muted-foreground mt-1">Daily fantasy projections — floor, ceiling, confidence & matchup ranks for the slate</p>
            </div>
            <Button onClick={fetchData} variant="outline" className="border-[#1e3a5f] text-white hover:bg-[#112240]" disabled={refreshing} data-testid="refresh-wnba-projections-btn">
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="space-y-4">{[1, 2, 3].map(i => <div key={i} className="h-40 skeleton rounded-lg" />)}</div>
          ) : !data || !data.players?.length ? (
            <Card className="bg-[#0f1e36] border-[#1e3a5f]"><CardContent className="p-12 text-center text-muted-foreground">No projections available right now.</CardContent></Card>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="wnba-projections-summary">
                <Badge variant="outline" className="border-orange-400 text-orange-400 text-sm px-3 py-1">
                  {data.date}
                </Badge>
                <Badge variant="outline" className="border-brand-cyan text-brand-cyan text-sm px-3 py-1">
                  {data.games_count} games
                </Badge>
                <Badge variant="outline" className="border-[#1e3a5f] text-white text-sm px-3 py-1">
                  {data.players_count} players projected
                </Badge>
              </div>

              <Tabs defaultValue="projections">
                <TabsList className="bg-[#0f1e36] border border-[#1e3a5f] p-1 mb-6 flex-wrap h-auto">
                  {['projections', 'games'].map(t => (
                    <TabsTrigger key={t} value={t} className="data-[state=active]:bg-brand-cyan data-[state=active]:text-[#0a1628] uppercase font-bold" data-testid={`wnba-proj-tab-${t}`}>
                      {t}
                    </TabsTrigger>
                  ))}
                </TabsList>

                <TabsContent value="projections">
                  <Input
                    placeholder="Search player or team abbr..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="bg-[#0f1e36] border-[#1e3a5f] text-white mb-4 max-w-sm"
                    data-testid="wnba-proj-search-input"
                  />
                  <Card className="bg-[#0f1e36] border-[#1e3a5f]">
                    <CardContent className="p-0 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-[11px] uppercase text-muted-foreground border-b border-[#1e3a5f]">
                            <th className="text-left py-3 px-4">#</th>
                            <th className="text-left py-3 px-2">Player</th>
                            <th className="text-center py-3 px-2">Pos</th>
                            <th className="text-center py-3 px-2">Matchup</th>
                            <th className="text-center py-3 px-2">Proj</th>
                            <th className="text-center py-3 px-2 hidden sm:table-cell">Floor</th>
                            <th className="text-center py-3 px-2 hidden sm:table-cell">Ceiling</th>
                            <th className="text-center py-3 px-2">Conf</th>
                            <th className="text-center py-3 px-2 hidden md:table-cell">Salary</th>
                            <th className="text-center py-3 px-2 hidden md:table-cell">Value</th>
                            <th className="text-center py-3 px-2 hidden lg:table-cell">Opp Rank</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.slice(0, 120).map((p, i) => (
                            <tr key={i} className="border-b border-[#1e3a5f]/50 hover:bg-[#112240] cursor-pointer" onClick={() => goToPlayer(p.name)} data-testid={`wnba-proj-row-${i}`}>
                              <td className="py-2 px-4 font-mono text-muted-foreground">{i + 1}</td>
                              <td className="py-2 px-2">
                                <div className="flex items-center gap-2 min-w-0">
                                  {p.headshot ? (
                                    <img src={p.headshot} alt={p.name} className="w-8 h-8 rounded-full bg-[#112240] object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                                  ) : <span>🏀</span>}
                                  <div className="min-w-0">
                                    <div className="text-white font-medium hover:text-brand-cyan truncate">{p.name}</div>
                                    {p.note && <div className="text-[10px] text-muted-foreground truncate max-w-[240px]">{p.note}</div>}
                                  </div>
                                </div>
                              </td>
                              <td className="text-center text-muted-foreground">{p.pos}</td>
                              <td className="text-center text-muted-foreground text-xs">{p.team} vs {p.opp}</td>
                              <td className="text-center font-mono font-bold text-brand-lime">{p.proj}</td>
                              <td className="text-center font-mono text-white hidden sm:table-cell">{p.floor}</td>
                              <td className="text-center font-mono text-white hidden sm:table-cell">{p.ceil}</td>
                              <td className={`text-center font-mono font-bold ${confColor(p.conf)}`}>{p.conf ?? '—'}</td>
                              <td className="text-center font-mono text-white hidden md:table-cell">{p.salary ? `$${p.salary.toLocaleString()}` : '—'}</td>
                              <td className="text-center font-mono text-brand-cyan hidden md:table-cell">{p.value ?? '—'}</td>
                              <td className={`text-center font-mono hidden lg:table-cell ${rankColor(p.opp_rank || 99)}`}>{p.opp_rank ?? '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="games">
                  <div className="grid md:grid-cols-2 gap-4">
                    {data.games.map((g, i) => (
                      <Card key={i} className="bg-[#0f1e36] border-[#1e3a5f]" data-testid={`wnba-proj-game-${i}`}>
                        <CardContent className="p-5">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <div className="font-heading text-lg font-bold text-white uppercase">{g.away} @ {g.home}</div>
                              <div className="text-xs text-muted-foreground">{g.game_info}</div>
                            </div>
                            <div className="text-right">
                              <div className="font-mono text-2xl font-bold text-brand-cyan">{g.total ?? '—'}</div>
                              <div className="text-[10px] text-muted-foreground uppercase">O/U Total</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            {g.spread_home != null && <span>{g.home} {g.spread_home > 0 ? '+' : ''}{g.spread_home}</span>}
                            {g.ml_away != null && <span>ML {g.ml_away > 0 ? '+' : ''}{g.ml_away} / {g.ml_home > 0 ? '+' : ''}{g.ml_home}</span>}
                            {g.status && <span className={g.status === 'Final' ? 'text-muted-foreground' : 'text-brand-lime'}>{g.status}</span>}
                            {g.away_score != null && g.status === 'Final' && (
                              <span className="font-mono text-white">{g.away} {g.away_score} — {g.home} {g.home_score}</span>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default WnbaProjections;
